import { useEffect, useState } from "react";
import type { AppSettings } from "@ai-pos/shared";
import { settingsApi } from "../api/resources.js";

export function StoreSettings() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => setSettings(res.settings))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  }, []);

  if (!settings) {
    return <div className="stack">{error ? <div className="alert alert-error">{error}</div> : "読み込み中..."}</div>;
  }

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await settingsApi.update({
        storeName: settings.storeName,
        storePostalCode: settings.storePostalCode,
        storeAddress: settings.storeAddress,
        storePhone: settings.storePhone,
        registrationNumber: settings.registrationNumber,
        businessDayCutoff: settings.businessDayCutoff,
        autoLockMinutes: settings.autoLockMinutes,
        receiptShowAddress: settings.receiptShowAddress,
        receiptShowRegistrationNumber: settings.receiptShowRegistrationNumber,
        receiptShowQrCode: settings.receiptShowQrCode,
        receiptFooterNote: settings.receiptFooterNote,
        officialReceiptDefaultDescription: settings.officialReceiptDefaultDescription,
        officialReceiptShowStampNotice: settings.officialReceiptShowStampNotice,
        orderParserProvider: settings.orderParserProvider,
      });
      setSettings(res.settings);
      setMessage("保存しました");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 640 }}>
      <h2>店舗設定</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="card stack">
        <div className="form-field">
          <label htmlFor="store-name">店舗名</label>
          <input id="store-name" value={settings.storeName} onChange={(e) => setSettings({ ...settings, storeName: e.target.value })} />
        </div>
        <div className="form-field">
          <label htmlFor="store-postal">郵便番号（領収書に印字）</label>
          <input
            id="store-postal"
            value={settings.storePostalCode}
            placeholder="1060045"
            onChange={(e) => setSettings({ ...settings, storePostalCode: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label htmlFor="store-address">店舗住所</label>
          <input id="store-address" value={settings.storeAddress} onChange={(e) => setSettings({ ...settings, storeAddress: e.target.value })} />
        </div>
        <div className="form-field">
          <label htmlFor="store-phone">電話番号</label>
          <input id="store-phone" value={settings.storePhone} onChange={(e) => setSettings({ ...settings, storePhone: e.target.value })} />
        </div>
        <div className="form-field">
          <label htmlFor="registration-number">登録番号（インボイス制度・任意）</label>
          <input
            id="registration-number"
            value={settings.registrationNumber}
            onChange={(e) => setSettings({ ...settings, registrationNumber: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label htmlFor="cutoff">営業日切替時刻（深夜営業を考慮した売上集計の区切り）</label>
          <input
            id="cutoff"
            type="time"
            value={settings.businessDayCutoff}
            onChange={(e) => setSettings({ ...settings, businessDayCutoff: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label htmlFor="auto-lock">無操作自動ロックまでの時間（分）</label>
          <input
            id="auto-lock"
            type="number"
            min={1}
            value={settings.autoLockMinutes}
            onChange={(e) => setSettings({ ...settings, autoLockMinutes: Number(e.target.value) })}
          />
        </div>
        <div className="form-field">
          <label htmlFor="parser-provider">AI/音声注文解析エンジン</label>
          <select
            id="parser-provider"
            value={settings.orderParserProvider}
            onChange={(e) => setSettings({ ...settings, orderParserProvider: e.target.value as AppSettings["orderParserProvider"] })}
          >
            <option value="rule">ルールベース（APIコストなし・既定）</option>
            <option value="anthropic">Anthropic API</option>
            <option value="mock">モック（開発用）</option>
          </select>
        </div>

        <h3>会計伝票の表示項目</h3>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.receiptShowAddress}
            onChange={(e) => setSettings({ ...settings, receiptShowAddress: e.target.checked })}
            style={{ minHeight: "auto", width: 20, height: 20 }}
          />
          店舗住所・電話番号を印字する
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.receiptShowRegistrationNumber}
            onChange={(e) => setSettings({ ...settings, receiptShowRegistrationNumber: e.target.checked })}
            style={{ minHeight: "auto", width: 20, height: 20 }}
          />
          登録番号を印字する
        </label>
        <div className="form-field">
          <label htmlFor="footer-note">伝票フッター備考</label>
          <input
            id="footer-note"
            value={settings.receiptFooterNote}
            onChange={(e) => setSettings({ ...settings, receiptFooterNote: e.target.value })}
          />
        </div>

        <h3>領収書の設定</h3>
        <div className="form-field">
          <label htmlFor="receipt-description">但し書きの既定値</label>
          <input
            id="receipt-description"
            value={settings.officialReceiptDefaultDescription}
            onChange={(e) => setSettings({ ...settings, officialReceiptDefaultDescription: e.target.value })}
          />
        </div>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.officialReceiptShowStampNotice}
            onChange={(e) => setSettings({ ...settings, officialReceiptShowStampNotice: e.target.checked })}
            style={{ minHeight: "auto", width: 20, height: 20 }}
          />
          5万円以上の領収書に収入印紙の注意書きを印字する
        </label>

        <button className="btn btn-primary btn-lg" onClick={() => void handleSave()} disabled={saving}>
          保存
        </button>
      </div>
    </div>
  );
}
